import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PageWrapper,
  Title,
  Note,
  Table,
  Row,
  HeaderRow,
  Cell,
  StatusPaid,
  StatusUnpaid,
  PayButton,
  DownloadButton,
  PaginationWrapper,
  NavButton,
  PageButton,
  SearchInput,
  FilterBar,
  Select,
  TableWrapper,
  HeaderRowAdmin,
  RowAdmin,
} from "../../../style/TransactionHistory_Style";
import PaymentService from "../../../services/api/payment.service";
import {
  EQrcodeRequest,
  IGetPageHistoryTransactionByUser,
} from "../../../models/payment";
import { toast } from "../../../utils/notify";
import { useTranslation } from "react-i18next";
import {
  Button,
  ButtonCloseModal,
  ButtonPaymentModal,
  ButtonSaveFileModal,
  CloseButton,
  PreviewContent,
  PreviewFooter,
  PreviewHeader,
  PreviewModal,
  PreviewOverlay,
  PreviewOverlayPreview,
  PreviewTitle,
  UploadedFileIcon,
  UploadedFileName,
  UploadedFileSize,
} from "../../../style/AIToolsPage_Style";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { envConfig } from "../../../config/env.config";
import {
  EPaymentStatus,
  HistoryTranslate,
  UploadFile,
} from "../../main/AiToolsPage";
import UserService from "../../../services/api/user.service";
import { Box, InputAdornment, TextField } from "@mui/material";
import TranslateService from "../../../services/api/translate.service";
import { ActionButton } from "../../../style/admin/Blog_Style";

export interface TransactionItem {
  id: string;
  fileName: string;
  fileSize: number; // bytes
  fromLang: string;
  toLang: string;
  isPaid: boolean;
}

const formatFileSize = (bytes: number) => {
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AdminTransactionHistory: React.FC = () => {
  const PAGE_SIZE = 10;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EPaymentStatus | "all">("all");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const previewRenderTokenRef = useRef(0);
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null);
  const [isFree, setIsFree] = useState<boolean>(true);
  const [isRenderingPreview, setIsRenderingPreview] = useState(false);

  const [totalPages, setTotalPages] = useState(0);
  const { t } = useTranslation();
  const [historyTranslate, setHistoryTranslate] = useState<HistoryTranslate>();
  const [historyTranslates, setHistoryTranslates] =
    useState<HistoryTranslate[]>();

  useEffect(() => {
    GetPageHistoryTransactionByUser();
  }, [page, search, status]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const GetPageHistoryTransactionByUser = async () => {
    const params: IGetPageHistoryTransactionByUser = {
      CurrentPage: page,
      PageSize: PAGE_SIZE,
      KeySearch: search,
      Status: status === "all" ? undefined : status,
    };

    const res = await UserService.getPageFile(params);

    if (res.success) {
      setHistoryTranslates(res.items);
      setTotalCount(res.totalCount);
      setTotalPages(res.totalPages); // 👈 backend trả về
    } else {
      toast(res.message, "error");
    }
  };

  const onclickReviewFile = async (id: string) => {
    const previewRequestToken = previewRenderTokenRef.current;

    try {
      // 🔥 TỰ ĐỘNG MỞ PREVIEW MODAL
      const filePreview = await TranslateService.getFilePreview(id);

      if (previewRequestToken !== previewRenderTokenRef.current) return;
      setPreviewFile({
        id: filePreview.data.id,
        file: new File([], filePreview.data.fileName), // dummy file
        status: filePreview.data.status,
        progress: 100,
        isTranslating: false,
        fileSize: filePreview.data.fileSize,
        orderId: filePreview.data.orderId,
        price: filePreview.data.price,
        previewBase64: buildPreviewImageUrl(filePreview.data.filePreviewBase64),
        fileBase64: filePreview.data.fileDownloadBase64,
        fileName: filePreview.data.fileName,
        numberOfTranslationServicesUsed:
          filePreview.data.numberOfTranslationServicesUsed,
        totalNumberOfTranslationServicesUsed:
          filePreview.data.totalNumberOfTranslationServicesUsed,
      });
      debugger;
      if (filePreview.data.status === EPaymentStatus.free) {
        setIsFree(true);
      } else {
        setIsFree(false);
      }

      setShowPreviewModal(true);
    } catch (error) {
      console.error("Preview error:", error);
      closePreviewModal();
      toast(t("error.preview_fail") || t("error.translate_fail"), "error");
    }
  };

  const buildPreviewImageUrl = (path?: string) => {
    if (!path) return "";

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const baseUrl = envConfig.API_ENDPOINT.replace(/\/api\/?$/i, "").replace(
      /\/$/g,
      "",
    );

    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    return encodeURI(`${baseUrl}${cleanPath}`);
  };

  const downloadBase64File = (base64: string, fileName: string) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "application/octet-stream";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const blob = new Blob([u8arr], { type: mime });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName; // 👈 tên file tải về
    document.body.appendChild(a);
    a.click(); // 👈 trigger download
    document.body.removeChild(a);

    URL.revokeObjectURL(url); // cleanup
  };

  const handleGetQrcode = async (orderId: string, price: number) => {
    const data: EQrcodeRequest = {
      orderId: orderId,
      price: price, //previewFile.price ?? 0,
    };

    const qrcode = await PaymentService.GetQrCode(data);

    if (qrcode.success) {
      window.open(qrcode.data, "_blank");
    } else {
      toast(t("transaction.getQrFail"), "error");
    }
  };

  const formatVND = (value: number): string => {
    return new Intl.NumberFormat("vi-VN").format(value);
  };

  const formatDate = (date?: string | Date): string => {
    if (!date) return "";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const createOrder = async (
    customOrderId: string | undefined,
    price?: number,
  ) => {
    const res = await fetch(
      envConfig.API_ENDPOINT +
        `/paypal/create-order?customOrderId=${customOrderId}&price=${price}`,
      {
        method: "POST",
      },
    );

    if (!res.ok) {
      toast("Tạo thanh toán thất bại", "error");
      return;
    }

    const data = await res.json();

    return data.id;
  };

  const onApprove = async (data: any, customOrderId?: string) => {
    await fetch(
      envConfig.API_ENDPOINT +
        `/paypal/capture-order?orderId=${data.orderID}&customOrderId=${customOrderId}`,
      { method: "POST" },
    );

    toast("Thanh toán PayPal thành công", "success");

    setShowPaymentModal(false);

    window.open(
      "https://docme.vn/transaction?orderId=" + customOrderId,
      "_blank",
    );
  };

  const getPagination = (current: number, total: number) => {
    const delta = 2; // số page xung quanh current
    const range: (number | string)[] = [];

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    // luôn có trang 1
    range.push(1);

    if (left > 2) {
      range.push("...");
    }

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < total - 1) {
      range.push("...");
    }

    // luôn có trang cuối
    if (total > 1) {
      range.push(total);
    }

    return range;
  };

  const GetFileHistoryTranslate = async (id: string) => {
    const res = await PaymentService.GetFileHistoryTranslate(id);

    if (res.success) {
      await downloadUrlFile(res.data.fileDownloadBase64, res.data.fileName);
      toast(res.message, "success");
    } else {
      toast(res.message, "error");
    }
  };

  const downloadUrlFile = async (fileUrl: string, fileName?: string) => {
    const finalUrl = buildFileUrl(fileUrl);

    if (!finalUrl) {
      toast(t("file.downloadUrlNotFound"), "error");
      return;
    }

    try {
      const response = await fetch(finalUrl);

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName || "translated-file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback nếu bị CORS hoặc server không cho fetch trực tiếp
      window.open(finalUrl, "_blank");
    }
  };

  const buildFileUrl = (path?: string) => {
    if (!path) return "";

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const baseUrl = envConfig.API_ENDPOINT.replace(/\/api\/?$/i, "").replace(
      /\/$/g,
      "",
    );

    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    return encodeURI(`${baseUrl}${cleanPath}`);
  };

  const closePreviewModal = () => {
    previewRenderTokenRef.current += 1; // chặn async render cũ append canvas sau khi đóng
    setShowPreviewModal(false);
    setTimeout(clearPreviewContainer, 0);
  };

  const clearPreviewContainer = () => {
    const container = document.getElementById("pdf-preview-container");
    if (container) container.innerHTML = "";
  };

  const formatFileSizeMB = (size: number) => {
    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <PageWrapper>
      <Title>
        {t("transaction.title")} - tổng {totalCount}
      </Title>

      <FilterBar>
        <SearchInput
          placeholder={t("transaction.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as EPaymentStatus | "all")}>
          <option value="all">{t("transaction.status.all")}</option>
          <option value={EPaymentStatus.translate_Completed}>
            {t("transaction.status.completed")}
          </option>
          <option value={EPaymentStatus.translate_Processing}>
            {t("transaction.status.processing")}
          </option>
          <option value={EPaymentStatus.success}>
            {t("transaction.status.paid")}
          </option>
          <option value={EPaymentStatus.free}>
            {t("transaction.status.free")}
          </option>
          <option value={EPaymentStatus.pending}>
            {t("transaction.status.unpaid")}
          </option>
        </Select>
      </FilterBar>
      <TableWrapper>
        <Table>
          <HeaderRowAdmin>
            <Cell>{t("transaction.table.fileName")}</Cell>
            <Cell>{t("transaction.table.fileSize")}</Cell>
            <Cell>{t("transaction.table.language")}</Cell>
            <Cell>{t("transaction.table.status")}</Cell>
            <Cell>{t("transaction.table.price")}</Cell>
            <Cell>{t("transaction.table.expirationDate")}</Cell>
            <Cell>Ngày tạo</Cell>
            <Cell></Cell>
            {/* <Cell>{t("transaction.table.action")}</Cell> */}
          </HeaderRowAdmin>

          {historyTranslates &&
            historyTranslates.map((item) => (
              <RowAdmin key={item.id}>
                <Cell>{item.fileName}</Cell>
                <Cell>{formatFileSize(item.fileSize)}</Cell>
                <Cell>
                  {item.from} → {item.to}
                </Cell>
                <Cell>
                  {item.status === EPaymentStatus.success ||
                  item.status === EPaymentStatus.translate_Completed ||
                  item.status === EPaymentStatus.translate_Processing ? (
                    <StatusPaid>{t("transaction.status.paid")}</StatusPaid>
                  ) : item.status === EPaymentStatus.free ? (
                    <>
                      {" "}
                      <StatusPaid>{t("transaction.status.free")}</StatusPaid>
                    </>
                  ) : (
                    <StatusUnpaid>
                      {t("transaction.status.unpaid")}
                    </StatusUnpaid>
                  )}
                </Cell>
                <Cell>{item?.price && formatVND(item?.price)} VNĐ</Cell>
                <Cell>
                  {item?.expirationDate && formatDate(item?.expirationDate)}
                </Cell>
                <Cell>
                  {item?.createdDate && formatDate(item?.createdDate)}
                </Cell>
                <Cell>
                  <ActionButton
                    onClick={() => {
                      onclickReviewFile(item.orderId);
                    }}>
                    Xem File
                  </ActionButton>
                </Cell>
                {/* <Cell>
                  {item.status === EPaymentStatus.success ? (
                    <DownloadButton
                      onClick={() =>
                        item?.status === EPaymentStatus.success
                          ? item?.fileDownloadBase64 &&
                            downloadBase64File(
                              item.fileDownloadBase64,
                              item.fileName,
                            )
                          : toast(
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  fill="currentColor"
                                  viewBox="0 0 16 16">
                                  <path d="M7.005 3.1a1 1 0 1 1 1.99 0l-.388 6.35a.61.61 0 0 1-1.214 0z" />
                                  <path d="M7 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0" />
                                </svg>
                                <span>{t("transaction.mustPayFirst")}</span>
                              </>,
                              "info",
                            )
                      }>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        className="bi bi-download">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                      </svg>
                      {t("transaction.download")}
                    </DownloadButton>
                  ) : (
                    <PayButton
                      onClick={() => {
                        setHistoryTranslate(item);
                        setShowPaymentModal(true);
                      }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        strokeWidth="0.8"
                        className="bi bi-credit-card"
                        viewBox="0 0 16 16">
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1z" />
                        <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
                      </svg>
                      {t("transaction.pay")}

                    </PayButton>
                  )}
                </Cell> */}
              </RowAdmin>
            ))}
        </Table>
      </TableWrapper>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationWrapper>
          <NavButton disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t("pagination.prev")}
          </NavButton>

          {getPagination(page, totalPages).map((item, i) =>
            item === "..." ? (
              <span key={i} style={{ padding: "0 8px" }}>
                ...
              </span>
            ) : (
              <PageButton
                key={i}
                active={page === item}
                onClick={() => setPage(item as number)}>
                {item}
              </PageButton>
            ),
          )}

          <NavButton
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}>
            {t("pagination.next")}
          </NavButton>
        </PaginationWrapper>
      )}

      {showPaymentModal && (
        <PreviewOverlay>
          <PreviewModal style={{ maxWidth: 420 }}>
            <PreviewHeader>
              <PreviewTitle>{t("preview.choose_payment_method")}</PreviewTitle>
              <CloseButton onClick={() => setShowPaymentModal(false)}>
                ✕
              </CloseButton>
            </PreviewHeader>

            <PreviewContent
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                padding: 20,
              }}>
              {/* VietQR */}
              <ButtonPaymentModal
                sx={{ justifyContent: "center" }}
                onClick={() => {
                  historyTranslate &&
                    handleGetQrcode(
                      historyTranslate.orderId,
                      historyTranslate.price,
                    );
                }}>
                <img
                  style={{
                    height: "20px",
                    filter: "drop-shadow(0px 1px 1px white)",
                  }}
                  src="/icon/VietQR.46a78cbb.png"
                />{" "}
                🇻🇳 Thanh toán VietQR
              </ButtonPaymentModal>

              {/* PayPal */}
              <div style={{ marginTop: 10 }}>
                <PayPalButtons
                  style={{ layout: "vertical" }}
                  createOrder={() =>
                    createOrder(
                      historyTranslate?.orderId,
                      historyTranslate?.price,
                    )
                  }
                  onApprove={(data) =>
                    onApprove(data, historyTranslate?.orderId)
                  }
                />
              </div>
            </PreviewContent>
          </PreviewModal>
        </PreviewOverlay>
      )}
      {showPreviewModal && (
        <PreviewOverlayPreview>
          <PreviewModal>
            <PreviewHeader>
              <PreviewTitle>{t("preview.title")}</PreviewTitle>
              <CloseButton onClick={closePreviewModal}>✕</CloseButton>
            </PreviewHeader>

            {/* <Box sx={{ paddingTop: "10px", fontSize: "15px" }}>
              <b>
                <i>
                  {t("note.review_note_before")}{" "}
                  <span style={{ color: "red" }}>
                    {Math.max(
                      (previewFile?.totalNumberOfTranslationServicesUsed ?? 0) -
                        (previewFile?.numberOfTranslationServicesUsed ?? 0),
                      0,
                    )}{" "}
                  </span>
                  {t("note.review_note_after")}
                </i>{" "}
              </b>
            </Box> */}

            {isFree === false ? (
              <PreviewContent
                style={{
                  position: "relative",
                  minHeight: 430,
                }}>
                {isRenderingPreview && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "rgba(255,255,255,.92)",
                      backdropFilter: "blur(6px)",
                      zIndex: 999,
                      gap: 24,
                    }}>
                    <div
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        border: "5px solid #dbeafe",
                        borderTop: "5px solid #2563eb",
                        animation: "spin 1s linear infinite",
                      }}
                    />

                    <div
                      style={{
                        width: 260,
                        height: 6,
                        background: "#e5e7eb",
                        borderRadius: 999,
                        overflow: "hidden",
                      }}>
                      <div
                        style={{
                          width: "40%",
                          height: "100%",
                          borderRadius: 999,
                          background:
                            "linear-gradient(90deg,#2563eb,#60a5fa,#2563eb)",
                          animation: "loadingBar 1.2s infinite ease-in-out",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: "#1e3a8a",
                      }}>
                      {t("preview.loading_title")}
                      <span className="loading-dots">...</span>
                    </div>

                    <div
                      style={{
                        color: "#6b7280",
                        fontSize: 14,
                      }}>
                      {t("preview.loading_desc")}
                    </div>
                  </div>
                )}

                <div id="pdf-preview-container">
                  {previewFile?.previewBase64 && (
                    <img
                      src={previewFile.previewBase64}
                      alt={previewFile.fileName || "Preview"}
                      onLoad={() => setIsRenderingPreview(false)}
                      onError={() => {
                        setIsRenderingPreview(false);
                        toast(
                          t("error.preview_fail") || t("error.translate_fail"),
                          "error",
                        );
                      }}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 12,
                        background: "#ffffff",
                      }}
                    />
                  )}
                </div>
              </PreviewContent>
            ) : (
              <div
                style={{
                  marginTop: 18,
                  padding: "28px 24px",
                  borderRadius: 16,
                  border: "1px solid #e2e8f0",
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #ffffff 55%, #f5f3ff 100%)",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
                }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                    padding: "18px 20px",
                    borderRadius: 14,
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
                  }}>
                  <UploadedFileIcon
                    fileType={
                      previewFile?.fileName?.split(".").pop()?.toLowerCase() ||
                      "file"
                    }>
                    <span>
                      {previewFile?.fileName
                        ?.split(".")
                        .pop()
                        ?.toUpperCase()
                        .slice(0, 4) || "FILE"}
                    </span>
                  </UploadedFileIcon>

                  <div
                    style={{
                      minWidth: 0,
                      flex: 1,
                    }}>
                    <UploadedFileName
                      title={previewFile?.fileName}
                      style={{
                        maxWidth: "100%",
                        marginBottom: 7,
                      }}>
                      {previewFile?.fileName || "Document"}
                    </UploadedFileName>

                    {previewFile?.fileSize ? (
                      <UploadedFileSize
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}>
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                        </svg>

                        {formatFileSizeMB(previewFile.fileSize)}
                      </UploadedFileSize>
                    ) : null}

                    <div
                      style={{
                        marginTop: 10,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "5px 10px",
                        borderRadius: 999,
                        background: "#ecfdf5",
                        color: "#15803d",
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="m9 12 2 2 4-4" />
                        <circle cx="12" cy="12" r="9" />
                      </svg>

                      {t("translationMode.free.title")}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <PreviewFooter>
              {/* <i className="footer-item-i">
                <img
                  style={{
                    filter:
                      "invert(17%) sepia(96%) saturate(7492%) hue-rotate(358deg) brightness(95%) contrast(112%)",
                  }}
                  width={30}
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAACXBIWXMAAAsTAAALEwEAmpwYAAADLUlEQVR4nO2YW4hNURjHf2MuucygiJmU5JIXFMIULxMPGl7IpcjgxVAYl7yIBxQKD3gYPLhElIRccsklNSGM3MaDW8gYd7mEGWO2Vv67lm3O6Zyz9z7nqP2r1Tn722t93/r2+tZa31oQERERERERERhtgErgNtAIOCGXJqAOWAzkBecG7ElD550Y5USqzhQBm4DHwFPgghR+BSqAQsKnEJgNfJHtpckqGAA8iPFl5pJ+Zsr2/WQajQI+qOEtYDBQAjRLZkYq3bSX7Z+JVM7R0DWp0XEpcHGypMSlizpuKrYAm1uZWFnvyHDgiSp9AqbEqJe1juQAVVYoXQd6x3E4Kx0pAc5aobQRyI83bInGaIg4XvvjgTcSvtVzSorSjOPabwts1QgYwRmNTNKK4pALlAFbFKr3gJ1AP38+/G3/pv58BxZpjhCQI/nagWNtombk+yRpL6b9Bm1wA0mN1hwpUkL3zHpvUprVQKlG4ojk+1K0+4/9fGWvBOBIMbDWygAcZakzWtl/+uq9qesHJ6g56iraAfywni8B4+KEamfVawrIvi+GeWL+F3AIGJFA2wq1uZNJR3KAY5aSb0C1wiURzAR/obYLyaAjecAr4D2wBuiWRNuRQL2MXwnghOf4Da0OQEES9XvqJOnuV9eArn46wJ+P4IZ06AwBdlm5m/ldB7QLQHepdJplPjTGArXW0Ddrz+gfgO5cLSh10r2BkJhsjUDY5S7QMQwnpuvoGWbnW4DnugTpFIYTlZp4xtgqYJb+H/DUq5Z8gecg5ygvyxgmxVlurUhmSTas0PN6T/2TkpdbsiWSmQ017ZgjwCTgqrUMmtOly37J53na1Ug+2lpGTaw7ypZ9dai7stKhwBhgopSajq3UyrAdOAic0vWody5clL4CJY+u3CSNNuesThu72/Rc72dZvuxj0jWq81W6aTSyl8BnT729SmtMmW+FoONJc8r8jEaNOmSOuY+AG8B54DCwW1dAJuaXAXOAqToGD9JIukwD3lmrSq1uAl0HG3TF6r4/DbwGPgJHpS9rMBtVL6XoLuX6QO6XfwhM4D+mGOjh8xAXERERERERQRx+A1Ns/jikzsHQAAAAAElFTkSuQmCC"
                  alt="card-in-use"></img>{" "}
                {t("preview.note")}
              </i> */}

              <div className="footer-item-div">
                {isFree && (
                  <>
                    <ButtonSaveFileModal
                      onClick={() => {
                        previewFile && GetFileHistoryTranslate(previewFile?.id);
                      }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        className="bi bi-download">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                      </svg>
                      {t("preview.download")}
                    </ButtonSaveFileModal>
                  </>
                )}

                <ButtonCloseModal onClick={closePreviewModal}>
                  {t("common.close")}
                </ButtonCloseModal>
              </div>
            </PreviewFooter>
          </PreviewModal>
        </PreviewOverlayPreview>
      )}
    </PageWrapper>
  );
};
